import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, Search, Globe } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import DataTable from '../../components/admin/DataTable';
import FormModal from '../../components/admin/FormModal';

interface SEOData {
  id: string;
  neighborhood_id: string;
  title: string;
  description: string;
  keywords: string;
  neighborhood: {
    name: string;
    district: {
      name: string;
      city: {
        name: string;
      };
    };
  };
}

export default function SEOSettings() {
  const [seoData, setSEOData] = useState<SEOData[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingData, setEditingData] = useState<any>(null);
  const [formValues, setFormValues] = useState<any>({});

  const columns = [
    { 
      key: 'neighborhood',
      label: 'Mahalle',
      render: (item: any) => item?.name
    },
    { 
      key: 'district',
      label: 'İlçe',
      render: (item: any) => item?.district?.name
    },
    { 
      key: 'city',
      label: 'İl',
      render: (item: any) => item?.district?.city?.name
    },
    { key: 'title', label: 'Başlık' },
    { key: 'description', label: 'Açıklama' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load neighborhoods
      const { data: neighborhoodsData, error: neighborhoodsError } = await supabase
        .from('neighborhoods')
        .select(`
          id,
          name,
          district:districts (
            id,
            name,
            city:cities (
              id,
              name
            )
          )
        `)
        .order('name');

      if (neighborhoodsError) throw neighborhoodsError;
      setNeighborhoods(neighborhoodsData || []);

      // Load SEO data
      const { data: seoData, error: seoError } = await supabase
        .from('neighborhood_seo')
        .select(`
          *,
          neighborhood:neighborhoods (
            id,
            name,
            district:districts (
              id,
              name,
              city:cities (
                id,
                name
              )
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (seoError) throw seoError;
      setSEOData(seoData || []);

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const formFields = [
    { 
      name: 'neighborhood_id', 
      label: 'Mahalle', 
      type: 'select',
      options: neighborhoods.map(n => ({ 
        value: n.id, 
        label: `${n.name} - ${n.district.name}, ${n.district.city.name}`
      }))
    },
    { name: 'title', label: 'Sayfa Başlığı', type: 'text' },
    { name: 'description', label: 'Meta Açıklama', type: 'textarea' },
    { name: 'keywords', label: 'Anahtar Kelimeler', type: 'text' }
  ];

  const handleEdit = (data: any) => {
    setEditingData(data);
    setFormValues({
      neighborhood_id: data.neighborhood_id,
      title: data.title,
      description: data.description,
      keywords: data.keywords
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (data: any) => {
    if (!window.confirm('Bu SEO ayarını silmek istediğinize emin misiniz?')) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('neighborhood_seo')
        .delete()
        .eq('id', data.id);

      if (error) throw error;
      await loadData();
    } catch (err) {
      console.error('Error deleting SEO data:', err);
      setError('SEO ayarı silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingData(null);
    setFormValues({});
    setIsModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      setError(null);

      if (editingData) {
        const { error } = await supabase
          .from('neighborhood_seo')
          .update({
            neighborhood_id: values.neighborhood_id,
            title: values.title,
            description: values.description,
            keywords: values.keywords
          })
          .eq('id', editingData.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('neighborhood_seo')
          .insert({
            neighborhood_id: values.neighborhood_id,
            title: values.title,
            description: values.description,
            keywords: values.keywords
          });

        if (error) throw error;
      }

      await loadData();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving SEO data:', err);
      setError('SEO ayarları kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl shadow-lg shadow-blue-400/20">
            <Globe className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">SEO Ayarları</h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      <DataTable
        columns={columns}
        data={seoData}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
      />

      <FormModal
        title={editingData ? 'SEO Ayarlarını Düzenle' : 'Yeni SEO Ayarı Ekle'}
        fields={formFields}
        values={formValues}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        onChange={(name, value) => setFormValues(prev => ({ ...prev, [name]: value }))}
      />
    </div>
  );
}